import React, { useLayoutEffect, useRef } from 'react';
import { Box, GlobalStyles } from '@mui/material';

/**
 * 富文本宽表格渲染容器（v2.2.30 + v2.2.31）
 *
 * ## 为什么需要它
 * 知识词条 / 文档正文是后端消毒过的 **HTML 字符串**，里面的 `<table>` 是原生 DOM，
 * 不是 React 组件。因此 `pages/fmea/FmeaTable.tsx` 那套「上下双横滚条 + 列宽按中文字宽重算」
 * 的增强**对它完全无效** —— 两者走的是两条路：
 *
 * | 展示位置 | 表格来源 | 渲染方式 | 9/8 增强是否生效 |
 * |---|---|---|---|
 * | `/fmea/library` 风险库 | 结构化 `FmeaItem` | React `FmeaTable` 组件 | ✅ |
 * | `/knowledge/article/:id` 词条 | `KnowledgeAsset.content_html` | 原生 `<table>` | ❌ ← 本组件补齐 |
 *
 * ## 做法
 * 渲染后扫描容器内所有 `<table>`，每个都套一层「顶部 mock 滚动条 + 底部真实滚动容器」，
 * 双向同步 `scrollLeft`。与 FmeaTable 同款 12px 滚动条、18px mock 高度、0.04 轨道底色。
 *
 * ## 关键 CSS（比 FmeaTable 更麻烦的一处）
 * 原 `SafeHtml` 里 table 是 `width: 100%`，14 列的 FMEA 表会被**强制压扁**到容器宽度，
 * 单元格疯狂换行、甚至单字竖排，而且连横向滚动都没有。
 * 这里改成 `width: max-content; min-width: 100%`：
 *   - 内容比容器宽 ⇒ `max-content` 生效，表格自然展开并产生横向滚动
 *   - 内容比容器窄 ⇒ `min-width: 100%` 生效，仍然撑满，观感与改动前一致
 *
 * ## 为什么用 DOM 操作而不是 React 组件
 * 正文是 `dangerouslySetInnerHTML` 灌进来的，React 不管理其内部节点；
 * 而一个词条里可能有**多张表**（FMEA 按项目阶段分段，每段一张）。
 * 用 ref + DOM 包装可以支持任意数量，且不被 React 渲染周期干扰。
 * 副作用：**完全可逆**（cleanup 会拆掉包装），支持 html 变化重跑与 StrictMode 双跑。
 *
 * ## v2.2.31 演进：CSS 从 sx 改成全局
 * 原先 `.wt-*` 规则写在组件 sx 里，emotion 会编译成 scoped 选择器（如 `.css-abc .wt-table`），
 * 而表格是被 `wrapTables()` 动态塞进 `.wt-viewport` 的 —— 一旦表格不在组件根的子树里
 * （或 emotion 类名重算），scoped 规则就失配，表现为"包了壳但样式没生效"（表格仍被压扁）。
 * 改为 `<GlobalStyles>` 注入后，`.wt-table` 与文档里任何位置都匹配。
 *
 * ## ⚠️ 编辑态不能用 DOM 包装（重要教训）
 * 编辑态是 ReactQuill，`onChange` 直接吐 `editor.root.innerHTML`。
 * 若在 contentEditable 里注入 `wt-wrap` / `wt-viewport`，
 * ① 这些壳会被当成正文**一起存进 content_html**，把文章 HTML 永久污染；
 * ② Quill 的 blot 模型会被外来 div 打乱，选区与撤销栈错乱。
 * 所以编辑态只用纯 CSS（`.ql-editor { overflow-x:auto }` + `table { width:max-content }`），
 * 一个 DOM 节点都不动。见 KnowledgeArticlePage 编辑态 sx。
 */

const CLS = {
  wrap: 'wt-wrap',
  mock: 'wt-mock',
  mockInner: 'wt-mock-inner',
  viewport: 'wt-viewport',
  table: 'wt-table',
};

/** 12px 滚动条 —— 与 FmeaTable 完全同款，避免两处观感不一致 */
const SCROLLBAR_SX = {
  '&::-webkit-scrollbar': { height: 12, width: 12 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.06)', borderRadius: 6 },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: 6 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(0,0,0,0.4) rgba(0,0,0,0.06)',
};

/**
 * v2.2.31：把 .wt-* CSS 抽成全局样式，让 wrapTables() 在任何容器内都生效。
 * 之前 CSS 是 sx scoped，只能影响 WideTableHtml 自己的根 Box；
 * 编辑态 RichTextEditor 里 wrapTables 后就没样式了。
 * 挂载：main.tsx 引入一次即可（或任意层级挂载，多次挂载无副作用）。
 */
export const WIDE_TABLE_GLOBAL_STYLES = {
  '.wt-wrap': { width: '100%', my: 1.5 },
  '.wt-mock': {
    overflowX: 'auto',
    overflowY: 'hidden',
    width: '100%',
    height: 18,                // 显式 18px：v2.2.25 用 1px 占位撑高，在高 DPI / overlay 滚动条下
    minHeight: 18,              //   会塌陷成一条看不见的细线 —— 用户视角等于"没修"。
    flexShrink: 0,
    border: '1px solid #e0e3e8',
    borderBottom: 'none',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    backgroundColor: 'rgba(0,0,0,0.04)', // 轨道底色，让它与内容区明显区分
    ...SCROLLBAR_SX,
  },
  '.wt-mock-inner': { height: 1, pointerEvents: 'none' },
  '.wt-viewport': {
    overflowX: 'auto',
    overflowY: 'hidden',
    width: '100%',
    borderTop: 'none',         // 顶部已有 mock，避免双重横线
    ...SCROLLBAR_SX,
  },
  '.wt-table': {
    // ★ 核心：不再被 width:100% 压扁
    //   内容宽 ⇒ max-content 展开并横向滚动；内容窄 ⇒ min-width:100% 撑满
    width: 'max-content',
    minWidth: '100%',
    mt: 0,
    mb: 0,
    borderCollapse: 'collapse',
  },
  '.wt-table th, .wt-table td': {
    border: '1px solid #d8dee9',
    px: '8px',
    py: '4px',
    fontSize: 13,
    textAlign: 'left',
  },
  '.wt-table th': { bgcolor: '#f5f7fa', fontWeight: 600, whiteSpace: 'nowrap' },
};

/**
 * 把这组件挂到应用根（main.tsx 已挂），CSS 就全局生效；
 * wrapTables() 调用到哪，.wt-* 样式就跟到哪。
 */
export const WideTableGlobalStyles: React.FC = () => (
  <GlobalStyles styles={WIDE_TABLE_GLOBAL_STYLES} />
);

/**
 * 给指定根容器内所有 `<table>` 加上下双横滚条包装。
 *
 * 幂等：已包装的（父级 class 含 `wt-viewport`）会跳过。
 * 返回 cleanup 函数，调用后拆掉包装、还原 DOM，可重入。
 *
 * 用于 v2.2.31 编辑态：KnowledgeArticlePage 用 MutationObserver 监听 RichTextEditor
 * (Quill) 内部 DOM 变化，每次变化后调用本函数。
 */
export function wrapTables(root: HTMLElement): () => void {
  if (!root) return () => {};
  const disposers: Array<() => void> = [];

  Array.from(root.querySelectorAll('table')).forEach((table) => {
    const originalParent = table.parentElement;
    if (!originalParent) return;
    // 已被上一轮包装（StrictMode 双跑 / MutationObserver 重跑）⇒ 跳过，避免套娃
    if (originalParent.classList.contains(CLS.viewport)) return;

    // ---- 建 DOM：wrap > (mock > mockInner) + (viewport > table) ----
    const wrap = document.createElement('div');
    wrap.className = CLS.wrap;

    const mock = document.createElement('div');
    mock.className = CLS.mock;
    mock.setAttribute('data-wt-top-scroll', 'true'); // 诊断标记，便于浏览器里定位
    const mockInner = document.createElement('div');
    mockInner.className = CLS.mockInner;
    mock.appendChild(mockInner);

    const viewport = document.createElement('div');
    viewport.className = CLS.viewport;

    originalParent.insertBefore(wrap, table);
    wrap.appendChild(mock);
    wrap.appendChild(viewport);
    viewport.appendChild(table);
    table.classList.add(CLS.table);

    // ---- 测宽：mock 占位宽度 = 表格真实宽度，保证上下滚动范围一致 ----
    // 取 viewport.scrollWidth 与 table 实际布局宽度的最大值（v2.2.26 教训：
    // scrollWidth 在拉伸/包裹下可能失真等于 clientWidth，会误判成"不溢出"）。
    const measure = () => {
      const tableW = Math.ceil(table.getBoundingClientRect().width);
      return Math.max(viewport.scrollWidth || 0, tableW);
    };
    const apply = () => {
      const w = measure();
      mockInner.style.width = `${w}px`;
      mock.setAttribute('data-wt-top-w', String(w));
    };
    apply();
    // 布局稳定后再确认一次（字体加载 / 图片撑开都会改变宽度）
    const raf = requestAnimationFrame(apply);

    const ro = new ResizeObserver(apply);
    ro.observe(viewport); // 容器宽度变化（侧边栏收起、窗口缩放）
    ro.observe(table);    // 表格内容宽度变化（列数、数据量）
    const onWinResize = () => apply();
    window.addEventListener('resize', onWinResize);

    // ---- 双向同步 scrollLeft：顶部 mock ↔ 底部 viewport ----
    let syncing = false;
    const sync = (src: HTMLElement, dst: HTMLElement) => {
      if (syncing) return;                 // 防递归：A 改 B 又触发 B 改 A
      syncing = true;
      dst.scrollLeft = src.scrollLeft;
      requestAnimationFrame(() => { syncing = false; });
    };
    const onTop = () => sync(mock, viewport);
    const onVp = () => sync(viewport, mock);
    mock.addEventListener('scroll', onTop, { passive: true });
    viewport.addEventListener('scroll', onVp, { passive: true });

    disposers.push(() => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', onWinResize);
      mock.removeEventListener('scroll', onTop);
      viewport.removeEventListener('scroll', onVp);
      // 拆掉包装，还原 DOM —— 保证 html 变化时能干净重建
      const host = wrap.parentElement;
      if (host) host.insertBefore(table, wrap);
      wrap.remove();
      table.classList.remove(CLS.table);
    });
  });

  return () => disposers.forEach((fn) => fn());
}

export interface WideTableHtmlProps {
  /** 后端已消毒的正文 HTML */
  html: string;
  /** 正文为空时的占位 HTML */
  placeholderHtml?: string;
  /**
   * 表头单元格强制不换行（默认 true）。
   * 富文本表格的表头通常很短，容器一窄就会被压成竖排（同 09-08 v2.2.27 修的问题）。
   */
  nowrapHead?: boolean;
  className?: string;
}

const WideTableHtml: React.FC<WideTableHtmlProps> = ({
  html,
  placeholderHtml = '<p style="color:#98a2b3">（暂无正文，点击「编辑」开始撰写）</p>',
  nowrapHead = true,
  className = 'knowledge-article-body',
}) => {
const rootRef = useRef<HTMLDivElement>(null);

  // v2.2.31：直接复用 wrapTables 纯函数（与编辑态 RichTextEditor 包装同源）。
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cleanup = wrapTables(root);
    return cleanup;
  }, [html]);

  return (
    <Box
      ref={rootRef}
      className={className}
      sx={{
        fontSize: 14,
        lineHeight: 1.8,
        '& p': { my: 0.75 },
        '& img': { maxWidth: '100%' },
        '& table:not(.wt-table)': { borderCollapse: 'collapse', width: '100%', my: 1 },
        // 单元格基础样式。包装后的表由全局 `.wt-table th/td` 接管；
        // 这里即便也命中，数值与全局一致，不会打架。
        '& th, & td': { border: '1px solid #d8dee9', px: 1, py: 0.5, fontSize: 13, textAlign: 'left' },
        '& th': { bgcolor: '#f5f7fa', fontWeight: 600 },
        // 注：.wt-* 规则在 main.tsx 用 <WideTableGlobalStyles /> 全局注入，不放在 sx 里，
        // 否则 emotion scoped 选择器可能匹配不到动态插入的包装节点。
      }}
      dangerouslySetInnerHTML={{ __html: html || placeholderHtml }}
    />
  );
};

export default WideTableHtml;
